import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
import { CalendarDateSchema } from "./meal-plan.js";
// ---------- Status ----------
export const CookSessionStatusSchema = z.enum([
    "active",
    "paused",
    "waiting",
    "completed",
    "abandoned",
]);
/** The three statuses for which the sparse GSI1 projection is written. */
export const OPEN_STATUSES = ["active", "paused", "waiting"];
// ---------- Embedded shapes ----------
export const StepStateSchema = z
    .object({
    status: z.enum(["active", "done"]),
    startedAt: ISODateSchema.optional(),
    completedAt: ISODateSchema.optional(),
})
    .passthrough();
export const ActiveTimerSchema = z
    .object({
    id: ULIDSchema("timer"),
    stepId: ULIDSchema("step"),
    label: z.string().max(40, "timer label must be ≤ 40 chars"),
    endsAt: ISODateSchema,
})
    .passthrough();
export const SessionNoteSchema = z
    .object({
    id: ULIDSchema("note"),
    stepId: ULIDSchema("step").nullable(),
    text: z.string().min(1).max(2000, "note text must be ≤ 2000 chars"),
    ts: ISODateSchema,
})
    .passthrough();
// stepStates is a map keyed by step id. z.record validates the values; we don't
// constrain the key beyond "non-empty string" (callers pass step ids).
const StepStatesSchema = z.record(z.string(), StepStateSchema);
// Links a cook session back to the plan slot it was started from (Plan 14, Daily Surfaces).
// Absent = session not started from a plan.
export const PlanRefSchema = z
    .object({ weekStartDate: CalendarDateSchema, slotId: ULIDSchema("slot") })
    .optional();
// ---------- DDB row ----------
const CurrentStepIdSchema = ULIDSchema("step").nullable();
export const CookSessionRowSchema = z
    .object({
    householdId: ULIDSchema("hh"),
    sk: ULIDSchema("sess"),
    // Sparse GSI keys — present ONLY while status ∈ {active, paused, waiting}.
    gsi1pk: z.string().optional(),
    gsi1sk: z.string().optional(),
    id: ULIDSchema("sess"),
    schemaVersion: z.literal(1),
    recipeId: ULIDSchema("rec"),
    status: CookSessionStatusSchema,
    currentStepId: CurrentStepIdSchema.default(null),
    stepStates: StepStatesSchema.default({}),
    checkedIngredients: z.array(ULIDSchema("ing")).default([]),
    activeTimers: z.array(ActiveTimerSchema).default([]),
    notes: z.array(SessionNoteSchema).default([]),
    wakeUpAt: ISODateSchema.nullable().default(null),
    wakeUpReason: z.string().max(200).nullable().default(null),
    wakeUpFiredAt: ISODateSchema.nullable().default(null),
    scheduleArn: z.string().nullable().default(null),
    scheduleName: z.string().nullable().default(null),
    startedAt: ISODateSchema,
    startedBy: UserIdSchema,
    completedAt: ISODateSchema.nullable().default(null),
    deletedAt: ISODateSchema.nullable().default(null),
    planRef: PlanRefSchema,
    // ttl present only on abandoned rows; passthrough tolerates either way.
})
    .passthrough();
// ---------- API item (row minus internal/secret keys) ----------
export const CookSessionApiItemSchema = z
    .object({
    id: ULIDSchema("sess"),
    schemaVersion: z.literal(1),
    recipeId: ULIDSchema("rec"),
    status: CookSessionStatusSchema,
    currentStepId: CurrentStepIdSchema.default(null),
    stepStates: StepStatesSchema.default({}),
    checkedIngredients: z.array(ULIDSchema("ing")).default([]),
    activeTimers: z.array(ActiveTimerSchema).default([]),
    notes: z.array(SessionNoteSchema).default([]),
    wakeUpAt: ISODateSchema.nullable().default(null),
    wakeUpReason: z.string().max(200).nullable().default(null),
    wakeUpFiredAt: ISODateSchema.nullable().default(null),
    startedAt: ISODateSchema,
    startedBy: UserIdSchema,
    completedAt: ISODateSchema.nullable().default(null),
    deletedAt: ISODateSchema.nullable().default(null),
    planRef: PlanRefSchema,
})
    .passthrough();
/**
 * Maps a raw DDB CookSession row to the API shape: re-parses through the row
 * schema, then strips the internal DDB keys (householdId, sk, gsi1pk, gsi1sk)
 * AND the server-only schedule fields (scheduleArn, scheduleName — never leaked
 * to clients). Unknown future keys round-trip via passthrough.
 */
export function cookSessionRowToApiShape(raw) {
    const row = CookSessionRowSchema.parse(raw);
    const merged = { ...row };
    for (const k of ["householdId", "sk", "gsi1pk", "gsi1sk", "scheduleArn", "scheduleName"]) {
        delete merged[k];
    }
    return CookSessionApiItemSchema.parse(merged);
}
// ---------- Request bodies ----------
export const CreateCookSessionRequestSchema = z
    .object({ recipeId: ULIDSchema("rec"), planRef: PlanRefSchema })
    .strict();
export const PatchCookSessionRequestSchema = z
    .object({
    currentStepId: CurrentStepIdSchema.optional(),
    stepStates: StepStatesSchema.optional(),
    checkedIngredients: z.array(ULIDSchema("ing")).optional(),
    activeTimers: z.array(ActiveTimerSchema).optional(),
    notes: z.array(SessionNoteSchema).optional(),
})
    .strict()
    .refine((v) => v.currentStepId !== undefined ||
    v.stepStates !== undefined ||
    v.checkedIngredients !== undefined ||
    v.activeTimers !== undefined ||
    v.notes !== undefined, { message: "at least one of currentStepId|stepStates|checkedIngredients|activeTimers|notes is required" });
export const WaitCookSessionRequestSchema = z
    .object({
    // DURATION in minutes — server computes wakeUpAt = now + pauseUntilMinutes.
    // Named with explicit unit to prevent a wall-clock misread (spec §3.2).
    pauseUntilMinutes: z
        .number()
        .int("pauseUntilMinutes must be an integer")
        .min(1, "pauseUntilMinutes must be ≥ 1")
        .max(10080, "pauseUntilMinutes must be ≤ 10080 (1 week)"),
    reason: z.string().min(1, "reason is required").max(200, "reason must be ≤ 200 chars"),
})
    .strict();
const RatingSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.null(),
]);
export const CompleteCookSessionRequestSchema = z
    .object({
    cookRating: RatingSchema.optional(),
    cookNote: z.string().max(2000, "cookNote must be ≤ 2000 chars").nullable().optional(),
    dishRating: RatingSchema.optional(),
    dishNote: z.string().max(2000, "dishNote must be ≤ 2000 chars").nullable().optional(),
})
    .strict();
// ---------- CookLog row ----------
/**
 * Per-step duration breakdown stored in `byStepDuration`.
 * `activeMin` is wall-clock minutes the user spent actively on this step;
 * `hadParallelOverlap` flags that the step ran concurrently with another.
 */
export const ByStepDurationSchema = z
    .object({
    activeMin: z.number().min(0),
    hadParallelOverlap: z.boolean().default(false),
})
    .passthrough();
export const CookLogRowSchema = z
    .object({
    householdId: ULIDSchema("hh"),
    sk: z.string(), // "<isoTimestamp>#<logId>"
    gsi1pk: z.string(), // "<householdId>#<recipeId>" (Plan 07 aggregator reader)
    gsi1sk: ISODateSchema, // cookedAt
    id: ULIDSchema("log"),
    schemaVersion: z.literal(1),
    recipeId: ULIDSchema("rec"),
    sessionId: ULIDSchema("sess"),
    cookedBy: UserIdSchema,
    cookedAt: ISODateSchema,
    completedAt: ISODateSchema,
    durationActiveMin: z.number().int().min(0).nullable().default(null),
    durationElapsedMin: z.number().int().min(0).nullable().default(null),
    durationOverride: z.number().int().min(0).nullable().default(null),
    leftoverPortions: z.number().int().min(0).max(20).nullable().default(null),
    byStepDuration: z.record(z.string(), ByStepDurationSchema).default({}),
    cookRating: RatingSchema.default(null),
    cookNote: z.string().max(2000).nullable().default(null),
    dishRating: RatingSchema.default(null),
    dishNote: z.string().max(2000).nullable().default(null),
    ratedAt: ISODateSchema.nullable().default(null),
    deletedAt: ISODateSchema.nullable().default(null),
})
    .passthrough();
// ---------- CookLog API item (row minus internal DDB keys) ----------
export const CookLogApiItemSchema = z
    .object({
    id: ULIDSchema("log"),
    schemaVersion: z.literal(1),
    recipeId: ULIDSchema("rec"),
    sessionId: ULIDSchema("sess"),
    cookedBy: UserIdSchema,
    cookedAt: ISODateSchema,
    completedAt: ISODateSchema,
    durationActiveMin: z.number().int().min(0).nullable().default(null),
    durationElapsedMin: z.number().int().min(0).nullable().default(null),
    durationOverride: z.number().int().min(0).nullable().default(null),
    leftoverPortions: z.number().int().min(0).max(20).nullable().default(null),
    byStepDuration: z.record(z.string(), ByStepDurationSchema).default({}),
    cookRating: RatingSchema.default(null),
    cookNote: z.string().max(2000).nullable().default(null),
    dishRating: RatingSchema.default(null),
    dishNote: z.string().max(2000).nullable().default(null),
    ratedAt: ISODateSchema.nullable().default(null),
    deletedAt: ISODateSchema.nullable().default(null),
})
    .passthrough();
/**
 * Maps a raw DDB CookLog row to the API shape: re-parses through the row
 * schema, then strips the 4 internal DDB keys (householdId, sk, gsi1pk,
 * gsi1sk). CookLog has no scheduleArn/scheduleName. Unknown future keys
 * round-trip via passthrough.
 */
export function cookLogRowToApiShape(raw) {
    const row = CookLogRowSchema.parse(raw);
    const merged = { ...row };
    for (const k of ["householdId", "sk", "gsi1pk", "gsi1sk"]) {
        delete merged[k];
    }
    return CookLogApiItemSchema.parse(merged);
}
// ---------- CookLog request bodies ----------
export const PatchCookLogRequestSchema = z
    .object({
    completedAt: ISODateSchema, // required — SK prefix to address the row
    cookRating: RatingSchema.optional(),
    dishRating: RatingSchema.optional(),
    cookNote: z.string().max(2000).nullable().optional(),
    dishNote: z.string().max(2000).nullable().optional(),
    durationOverride: z.number().int().min(0).nullable().optional(),
    leftoverPortions: z.number().int().min(0).max(20).optional(),
})
    .strict()
    .refine((v) => v.cookRating !== undefined ||
    v.dishRating !== undefined ||
    v.cookNote !== undefined ||
    v.dishNote !== undefined ||
    v.durationOverride !== undefined ||
    v.leftoverPortions !== undefined, {
    message: "at least one of cookRating|dishRating|cookNote|dishNote|durationOverride|leftoverPortions is required",
});
// ---------- CookLog list response ----------
export const CookLogListResponseSchema = z.object({
    items: z.array(CookLogApiItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=cook-session.js.map